import {
  CloudOff,
  MobileScreenShare,
  MoreVert,
  RemoveRedEye,
} from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  NotificationTypeEnum,
  useDialog,
  useMenu,
  useNotification,
} from '@prismamedia/one-components';
import { FC, MouseEvent, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useArticleUpdater } from '../../apollo/mutations/article.web.graphql';
import { useApolloClient } from '@apollo/client';
import { GET_ARTICLE_URL_STATUS } from '../../apollo/queries/articles.web.graphql';
import { ArticleFormat, ArticleStatus } from '../../__generated__/queries-web';
import {
  ERROR_MODIFY_STATUS,
  SEE_PUBLIC_SITE,
  SEND_PUSH,
  STATUS_TO_DRAFT,
  UNPUBLISH,
  UNPUBLISH_VIDEO,
} from './constants';
import { useStyles } from './styles';
import { PublicationStatusDialog } from '../PublicationStatusDialog';

interface ArticleMenuProps {
  articleId: string;
  articleFormat: ArticleFormat;
  publicUrl: string;
  brandKey: string;
  displayUnPublish: boolean;
  displaySeeArticle: boolean;
  displayPushNotification: boolean;
}

export const ArticleMenu: FC<ArticleMenuProps> = ({
  articleId,
  articleFormat,
  brandKey,
  displayUnPublish,
  displaySeeArticle,
  displayPushNotification,
  publicUrl,
}) => {
  const { openMenu, closeMenu } = useMenu();
  const { openDialog, closeDialog } = useDialog();
  const [upsert] = useArticleUpdater();
  const { pushNotification } = useNotification();
  const history = useHistory();
  const client = useApolloClient();

  const classes = useStyles();

  const executeUnpublish = useCallback(async () => {
    try {
      const props = {
        variables: {
          where: { id: articleId },
          data: {
            status:
              articleFormat === ArticleFormat.Video
                ? ArticleStatus.Deleted
                : ArticleStatus.Draft,
          },
        },
      };
      const result = await upsert(props);
      if (result?.data?.updateArticle) {
        pushNotification({
          message: STATUS_TO_DRAFT,
          type: NotificationTypeEnum.success,
        });
      }
    } catch {
      pushNotification({
        message: ERROR_MODIFY_STATUS,
        type: NotificationTypeEnum.error,
      });
    }
  }, [articleId, pushNotification, upsert, articleFormat]);

  const handleMutation = useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      closeMenu();

      openDialog(
        <>
          <DialogTitle>Dépublier un article</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ⚠️ Attention : Êtes-vous sûr(e) de vouloir dépublier cet article ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} color="primary" variant="outlined">
              Annuler
            </Button>
            <Button
              onClick={() => {
                executeUnpublish();
                closeDialog();
              }}
              color="primary"
              variant="contained"
              autoFocus
            >
              Oui
            </Button>
          </DialogActions>
        </>,
      );
    },
    [closeMenu, openDialog, closeDialog, executeUnpublish],
  );

  const handleMobilePush = () => {
    closeMenu();
    history.push(`/${brandKey}/mobilepush/add?id=${articleId}`);
  };

  const UnPublishItem = () => {
    return (
      <MenuItem
        classes={{ root: classes.actionsMenu }}
        data-testid="unPublish"
        onClick={handleMutation}
      >
        <ListItemIcon>
          <CloudOff fontSize="small" />
        </ListItemIcon>
        <Typography variant="inherit">
          {articleFormat === ArticleFormat.Video ? UNPUBLISH_VIDEO : UNPUBLISH}
        </Typography>
      </MenuItem>
    );
  };

  const handleDisplayItemClick = useCallback(async () => {
    closeMenu();

    if (config.ENV === 'REC2') {
      window.open(publicUrl);
      return;
    }

    try {
      const result = await client.query({
        query: GET_ARTICLE_URL_STATUS,
        variables: { where: { id: articleId } },
        fetchPolicy: 'network-only',
      });

      const urlStatus = result?.data?.article?.urls?.[0]?.status?.ok;

      if (urlStatus) {
        window.open(publicUrl);
      } else {
        openDialog(
          <PublicationStatusDialog url={publicUrl} onClose={closeDialog} />,
          {
            maxWidth: 'sm',
          },
        );
      }
    } catch (error) {
      // If there's an error fetching the status, show the dialog
      openDialog(
        <PublicationStatusDialog url={publicUrl} onClose={closeDialog} />,
        {
          maxWidth: 'sm',
        },
      );
    }
  }, [closeMenu, client, articleId, publicUrl, openDialog, closeDialog]);

  const DisplayItem = () => {
    return (
      <MenuItem
        classes={{ root: classes.actionsMenu }}
        data-testid="seeArticle"
        onClick={handleDisplayItemClick}
      >
        <ListItemIcon>
          <RemoveRedEye fontSize="small" />
        </ListItemIcon>
        <Typography variant="inherit">{SEE_PUBLIC_SITE}</Typography>
      </MenuItem>
    );
  };

  const SendPushItem = () => {
    return (
      <MenuItem
        classes={{ root: classes.actionsMenu }}
        data-testid="pushButton"
        onClick={handleMobilePush}
      >
        <ListItemIcon>
          <MobileScreenShare fontSize="small" />
        </ListItemIcon>
        <Typography variant="inherit">{SEND_PUSH}</Typography>
      </MenuItem>
    );
  };

  return (
    <IconButton
      arial-label="more"
      data-testid="menuBtn"
      onClick={(event) => {
        event.preventDefault();
        openMenu(
          event.currentTarget,
          <div>
            {displayUnPublish && <UnPublishItem />}
            {displaySeeArticle && <DisplayItem />}
            {displayPushNotification && <SendPushItem />}
          </div>,
        );
      }}
    >
      <MoreVert />
    </IconButton>
  );
};
